import React, { useState, useEffect } from 'react';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  Clock,
  Award,
  AlertCircle,
  FileText,
  Globe,
  Home,
  Grid,
  Package,
  FileType,
  BookOpen,
  Settings,
  Link,
  Plus,
  Trash2
} from 'lucide-react';
import { useSiteContext } from '../contexts/SiteContext';
import AuditReportSection from './AuditReportSection';

const PAGE_TYPES = [
  { id: 'homepage', label: 'Homepage', icon: Home },
  { id: 'category', label: 'Category', icon: Grid },
  { id: 'product', label: 'Product', icon: Package },
  { id: 'cms', label: 'CMS Page', icon: FileType },
  { id: 'blog', label: 'Blog Post', icon: BookOpen },
  { id: 'custom', label: 'Custom URL', icon: Link }
];

const AuditDashboard = ({ user }) => {
  const { selectedSite } = useSiteContext();
  const [activeTab, setActiveTab] = useState('homepage');
  const [auditData, setAuditData] = useState(null);
  const [websiteData, setWebsiteData] = useState(null);
  const [pageUrl, setPageUrl] = useState(null);
  const [setupRequired, setSetupRequired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [error, setError] = useState(null);
  const [expandedAudit, setExpandedAudit] = useState(null);
  const [customUrl, setCustomUrl] = useState('');
  const [addingCustomUrl, setAddingCustomUrl] = useState(false);
  const [deletingCustomUrl, setDeletingCustomUrl] = useState(false);
  const [showCustomUrlInput, setShowCustomUrlInput] = useState(false);
  const [pageTypeCustomUrl, setPageTypeCustomUrl] = useState('');

  useEffect(() => {
    if (selectedSite) {
      fetchAuditData();
    } else {
      setLoading(false);
      setError('Please select a site to view audit data');
    }
  }, [user, selectedSite, activeTab]);

  // Auto-expand first audit when data loads
  useEffect(() => {
    if (auditData && auditData.length > 0 && expandedAudit === null) {
      setExpandedAudit(auditData[0].id);
    }
  }, [auditData]);

  const fetchAuditData = async () => {
    if (!selectedSite) return;

    setLoading(true);
    setError(null);
    setSetupRequired(false);
    setExpandedAudit(null);
    // Clear audit data, page URL, and website summary when switching tabs
    setAuditData(null);
    setPageUrl(null);
    setWebsiteData(null);

    try {
      // Fetch both audits and website summary in parallel
      const [auditsResponse, websiteResponse] = await Promise.all([
        fetch(`/app/api/audit-portal/audits.php?site_id=${selectedSite.id}&page_type=${activeTab}`, {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`/app/api/audit-portal/website.php?site_id=${selectedSite.id}&page_type=${activeTab}`, {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      const auditsResult = await auditsResponse.json();
      const websiteResult = await websiteResponse.json();

      if (auditsResponse.ok && auditsResult.success) {
        setAuditData(auditsResult.data.data);
        setPageUrl(auditsResult.data.page_url || null);
        setSetupRequired(auditsResult.data.setup_required || false);
      } else {
        setError(auditsResult.message || 'Failed to fetch audit data');
      }

      if (websiteResponse.ok && websiteResult.success) {
        setWebsiteData(websiteResult.data.data);
      }

    } catch (error) {
      console.error('Error fetching audit data:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupPageAudits = async () => {
    if (!selectedSite || settingUp) return;

    setSettingUp(true);
    setError(null);

    try {
      const response = await fetch(`/app/api/audit-portal/setup-pages.php?site_id=${selectedSite.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Wait a bit for the audits to process, then fetch updated data
        setTimeout(() => {
          fetchAuditData();
        }, 3000);
      } else {
        setError(result.message || 'Failed to setup page audits');
      }
    } catch (error) {
      console.error('Error setting up page audits:', error);
      setError('Network error. Please try again.');
    } finally {
      setSettingUp(false);
    }
  };

  const handleRefreshAudit = async () => {
    if (!selectedSite || refreshing) return;

    setRefreshing(true);
    setError(null);

    try {
      const response = await fetch(`/app/api/audit-portal/refresh.php?site_id=${selectedSite.id}&page_type=${activeTab}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Wait a bit for the audit to process, then fetch updated data
        setTimeout(() => {
          fetchAuditData();
        }, 3000);
      } else {
        setError(result.message || 'Failed to refresh audit');
      }
    } catch (error) {
      console.error('Error refreshing audit:', error);
      setError('Network error. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleAddCustomUrl = async (e) => {
    e.preventDefault();
    if (!selectedSite || addingCustomUrl || !customUrl.trim()) return;

    setAddingCustomUrl(true);
    setError(null);

    try {
      const response = await fetch(`/app/api/audit-portal/custom-url.php?site_id=${selectedSite.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url: customUrl.trim() })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setCustomUrl('');
        // Wait a bit for the audit to process, then fetch updated data
        setTimeout(() => {
          fetchAuditData();
        }, 3000);
      } else {
        setError(result.message || 'Failed to add custom URL');
      }
    } catch (error) {
      console.error('Error adding custom URL:', error);
      setError('Network error. Please try again.');
    } finally {
      setAddingCustomUrl(false);
    }
  };

  const handleDeleteCustomUrl = async () => {
    if (!selectedSite || deletingCustomUrl) return;

    setDeletingCustomUrl(true);
    setError(null);

    try {
      const response = await fetch(`/app/api/audit-portal/custom-url.php?site_id=${selectedSite.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setPageUrl(null);
        setAuditData([]);
        setSetupRequired(true);
      } else {
        setError(result.message || 'Failed to remove custom URL');
      }
    } catch (error) {
      console.error('Error removing custom URL:', error);
      setError('Network error. Please try again.');
    } finally {
      setDeletingCustomUrl(false);
    }
  };

  const handleSetPageTypeCustomUrl = async (e) => {
    e.preventDefault();
    if (!selectedSite || addingCustomUrl || !pageTypeCustomUrl.trim()) return;

    setAddingCustomUrl(true);
    setError(null);

    try {
      const response = await fetch(`/app/api/audit-portal/set-page-url.php?site_id=${selectedSite.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          url: pageTypeCustomUrl.trim(),
          page_type: activeTab
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setPageTypeCustomUrl('');
        setShowCustomUrlInput(false);
        // Wait a bit for the audit to process, then fetch updated data
        setTimeout(() => {
          fetchAuditData();
        }, 3000);
      } else {
        setError(result.message || 'Failed to set custom URL');
      }
    } catch (error) {
      console.error('Error setting custom URL:', error);
      setError('Network error. Please try again.');
    } finally {
      setAddingCustomUrl(false);
    }
  };

  const handleDeletePageTypeUrl = async () => {
    if (!selectedSite || deletingCustomUrl) return;

    setDeletingCustomUrl(true);
    setError(null);

    try {
      const response = await fetch(`/app/api/audit-portal/set-page-url.php?site_id=${selectedSite.id}&page_type=${activeTab}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setPageUrl(null);
        setAuditData([]);
        setSetupRequired(true);
      } else {
        setError(result.message || 'Failed to remove URL');
      }
    } catch (error) {
      console.error('Error removing URL:', error);
      setError('Network error. Please try again.');
    } finally {
      setDeletingCustomUrl(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 70) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    if (score >= 50) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getScoreIcon = (score) => {
    if (score >= 90) return <CheckCircle className="w-12 h-12" />;
    if (score >= 70) return <TrendingUp className="w-12 h-12" />;
    if (score >= 50) return <AlertTriangle className="w-12 h-12" />;
    return <XCircle className="w-12 h-12" />;
  };


  if (!selectedSite) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">No Site Selected</h2>
            <p className="text-gray-500">Please select a site from the dropdown to view audit data.</p>
          </div>
        </div>
      </div>
    );
  }
  
  console.log(auditData);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Website Audit</h1>
            <p className="text-gray-600">Viewing audit for: <span className="font-semibold">{selectedSite.name}</span></p>
          </div>
          <div className="flex gap-2">
            {activeTab !== 'homepage' && setupRequired && (
              <button
                onClick={handleSetupPageAudits}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                disabled={loading || settingUp}
              >
                <Settings className={`w-4 h-4 ${settingUp ? 'animate-spin' : ''}`} />
                {settingUp ? 'Setting up...' : 'Setup Page Audits'}
              </button>
            )}
            <button
              onClick={handleRefreshAudit}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
              disabled={loading || refreshing || setupRequired}
            >
              <RefreshCw className={`w-4 h-4 ${(loading || refreshing) ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh Audit'}
            </button>
          </div>
        </div>

        {/* Page Type Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              {PAGE_TYPES.map((pageType) => {
                const Icon = pageType.icon;
                return (
                  <button
                    key={pageType.id}
                    onClick={() => setActiveTab(pageType.id)}
                    className={`
                      flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition
                      ${activeTab === pageType.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <Icon className="w-4 h-4" />
                    {pageType.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Page URL indicator */}
        {pageUrl && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-blue-700">
                <Globe className="w-4 h-4" />
                <span className="text-sm">Auditing: </span>
                <a href={pageUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-medium hover:underline">
                  {pageUrl}
                </a>
              </div>
              {activeTab === 'custom' && (
                <button
                  onClick={handleDeleteCustomUrl}
                  className="flex items-center gap-1 px-2 py-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition text-sm"
                  disabled={deletingCustomUrl}
                  title="Remove custom URL"
                >
                  <Trash2 className="w-4 h-4" />
                  {deletingCustomUrl ? 'Removing...' : 'Remove'}
                </button>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex items-center justify-center">
              <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mr-3" />
              <span className="text-gray-600">Loading audit data...</span>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error loading audit data</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Website Summary */}
            {websiteData && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Average Score</p>
                      <p className="text-3xl font-bold text-gray-800">{websiteData.score || 0}%</p>
                    </div>
                    <Award className="w-12 h-12 text-blue-500" />
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total Audits</p>
                      <p className="text-3xl font-bold text-gray-800">{websiteData.total_audits || 0}</p>
                    </div>
                    <FileText className="w-12 h-12 text-green-500" />
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total Issues</p>
                      <p className="text-3xl font-bold text-gray-800">{(websiteData.major_issues || 0) + (websiteData.moderate_issues || 0) + (websiteData.minor_issues || 0)}</p>
                    </div>
                    <AlertCircle className="w-12 h-12 text-purple-500" />
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Major Issues</p>
                      <p className="text-3xl font-bold text-gray-800">{websiteData.major_issues || 0}</p>
                    </div>
                    <XCircle className="w-12 h-12 text-red-500" />
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-yellow-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Moderate Issues</p>
                      <p className="text-3xl font-bold text-gray-800">{websiteData.moderate_issues || 0}</p>
                    </div>
                    <AlertTriangle className="w-12 h-12 text-yellow-500" />
                  </div>
                </div>
              </div>
            )}

            {/* Audits List */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800">Audit History</h2>
              </div>

              {auditData && auditData.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {auditData.map((audit_Wrap) => {
                    const audit = audit_Wrap.data || audit_Wrap;

                    return (

                  
                    <div key={audit.id} className="p-6 hover:bg-gray-50 transition">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-4 mb-3">
                            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${getScoreColor(audit.score)}`}>
                              {getScoreIcon(audit.score)}
                              <div>
                                <p className="text-sm font-medium">Score</p>
                                <p className="text-2xl font-bold">{audit.score}/100</p>
                              </div>
                            </div>

                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Globe className="w-4 h-4 text-gray-500" />
                                <a href={audit.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                  {audit.url}
                                </a>
                              </div>
                              {audit.title && (
                                <p className="text-sm text-gray-600 mb-1">
                                  <strong>Title:</strong> {audit.title}
                                </p>
                              )}
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {formatDate(audit.datetime)}
                                </span>
                                {audit.last_refresh_datetime && (
                                  <span className="flex items-center gap-1">
                                    <RefreshCw className="w-4 h-4" />
                                    Last refresh: {formatDate(audit.last_refresh_datetime)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Test Results */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                            <div className="bg-gray-50 rounded-lg p-3">
                              <p className="text-xs text-gray-600 mb-1">Tests Passed</p>
                              <p className="text-lg font-semibold text-green-600">{audit.passed_tests}/{audit.total_tests}</p>
                            </div>
                            <div className="bg-red-50 rounded-lg p-3">
                              <p className="text-xs text-gray-600 mb-1">Major Issues</p>
                              <p className="text-lg font-semibold text-red-600">{audit.major_issues || 0}</p>
                            </div>
                            <div className="bg-yellow-50 rounded-lg p-3">
                              <p className="text-xs text-gray-600 mb-1">Moderate Issues</p>
                              <p className="text-lg font-semibold text-yellow-600">{audit.moderate_issues || 0}</p>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-3">
                              <p className="text-xs text-gray-600 mb-1">Minor Issues</p>
                              <p className="text-lg font-semibold text-blue-600">{audit.minor_issues || 0}</p>
                            </div>
                          </div>

                          {/* Performance Metrics */}
                          {(audit.ttfb || audit.response_time || audit.page_size) && (
                            <div className="grid grid-cols-3 gap-4 mt-4">
                              {audit.ttfb && (
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <p className="text-xs text-gray-600 mb-1">TTFB</p>
                                  <p className="text-sm font-semibold text-gray-800">{audit.ttfb.toFixed(2)}s</p>
                                </div>
                              )}
                              {audit.response_time && (
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <p className="text-xs text-gray-600 mb-1">Response Time</p>
                                  <p className="text-sm font-semibold text-gray-800">{audit.response_time.toFixed(2)}s</p>
                                </div>
                              )}
                              {audit.page_size && (
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <p className="text-xs text-gray-600 mb-1">Page Size</p>
                                  <p className="text-sm font-semibold text-gray-800">{(audit.page_size / 1024).toFixed(2)} KB</p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Security Info */}
                          {(audit.is_https !== null || audit.is_ssl_valid !== null) && (
                            <div className="flex gap-2 mt-4">
                              {audit.is_https !== null && (
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                  audit.is_https ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  {audit.is_https ? 'HTTPS' : 'HTTP'}
                                </span>
                              )}
                              {audit.is_ssl_valid !== null && audit.is_https && (
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                  audit.is_ssl_valid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  {audit.is_ssl_valid ? 'Valid SSL' : 'Invalid SSL'}
                                </span>
                              )}
                            </div>
                          )}

                          {/* View Details Button */}
                          { audit.issues && (
                            <div className="mt-4">
                              <button
                                onClick={() => setExpandedAudit(expandedAudit === audit.id ? null : audit.id)}
                                className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center gap-2"
                              >
                                {expandedAudit === audit.id ? '▼ Hide' : '▶ View'} Detailed Issues Report
                              </button>
                            </div>
                          )}

                          {/* Detailed Issues Report */}
                          {expandedAudit === audit.id && audit.issues && (
                            <div className="mt-4 border-t pt-4">
                              <h3 className="text-lg font-semibold text-gray-800 mb-4">Detailed SEO Audit Report</h3>
                              <AuditReportSection audit={audit} />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                      )
                    })}
                </div>
              ) : setupRequired ? (
                <div className="p-8 text-center">
                  {activeTab === 'custom' ? (
                    <>
                      <Link className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Add Custom URL to Audit</h3>
                      <p className="text-gray-500 mb-6">
                        Enter any URL from your site to run an SEO audit on it.
                      </p>
                      <form onSubmit={handleAddCustomUrl} className="max-w-lg mx-auto">
                        <div className="flex gap-2">
                          <input
                            type="url"
                            value={customUrl}
                            onChange={(e) => setCustomUrl(e.target.value)}
                            placeholder={`${selectedSite.url}/your-page`}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            required
                          />
                          <button
                            type="submit"
                            className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                            disabled={addingCustomUrl || !customUrl.trim()}
                          >
                            <Plus className={`w-5 h-5 ${addingCustomUrl ? 'animate-spin' : ''}`} />
                            {addingCustomUrl ? 'Adding...' : 'Add & Audit'}
                          </button>
                        </div>
                        <p className="text-xs text-gray-400 mt-3">
                          URL must be within the site's domain: {selectedSite.url}
                        </p>
                      </form>
                    </>
                  ) : (
                    <>
                      <Settings className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Page Audit Not Configured</h3>
                      <p className="text-gray-500 mb-4">
                        {activeTab === 'category' && 'Category page audit needs to be set up. We\'ll find the category with the most products.'}
                        {activeTab === 'product' && 'Product page audit needs to be set up. We\'ll find a configurable product for testing.'}
                        {activeTab === 'cms' && 'CMS page audit needs to be set up. We\'ll look for About Us or similar pages.'}
                        {activeTab === 'blog' && 'Blog post audit needs to be set up. We\'ll find a blog post if available.'}
                      </p>

                      {/* Show custom URL option for product, category, and blog */}
                      {(activeTab === 'product' || activeTab === 'category' || activeTab === 'blog') && (
                        <div className="mb-6">
                          {!showCustomUrlInput ? (
                            <div className="flex flex-col items-center gap-4">
                              <button
                                onClick={handleSetupPageAudits}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                                disabled={settingUp}
                              >
                                <Settings className={`w-5 h-5 ${settingUp ? 'animate-spin' : ''}`} />
                                {settingUp ? 'Setting up page audits...' : 'Auto-detect Page'}
                              </button>
                              <span className="text-gray-400">or</span>
                              <button
                                onClick={() => setShowCustomUrlInput(true)}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                              >
                                <Link className="w-5 h-5" />
                                Enter Custom URL
                              </button>
                            </div>
                          ) : (
                            <form onSubmit={handleSetPageTypeCustomUrl} className="max-w-lg mx-auto">
                              <p className="text-sm text-gray-600 mb-3">
                                Enter the {activeTab} page URL you want to audit:
                              </p>
                              <div className="flex gap-2">
                                <input
                                  type="url"
                                  value={pageTypeCustomUrl}
                                  onChange={(e) => setPageTypeCustomUrl(e.target.value)}
                                  placeholder={`${selectedSite.url}/${activeTab === 'product' ? 'product-name.html' : activeTab === 'category' ? 'category-name' : 'blog/post-name'}`}
                                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                  required
                                />
                                <button
                                  type="submit"
                                  className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                                  disabled={addingCustomUrl || !pageTypeCustomUrl.trim()}
                                >
                                  <Plus className={`w-5 h-5 ${addingCustomUrl ? 'animate-spin' : ''}`} />
                                  {addingCustomUrl ? 'Adding...' : 'Add & Audit'}
                                </button>
                              </div>
                              <div className="flex justify-center mt-3">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowCustomUrlInput(false);
                                    setPageTypeCustomUrl('');
                                  }}
                                  className="text-sm text-gray-500 hover:text-gray-700"
                                >
                                  Cancel
                                </button>
                              </div>
                              <p className="text-xs text-gray-400 mt-2">
                                URL must be within the site's domain: {selectedSite.url}
                              </p>
                            </form>
                          )}
                        </div>
                      )}

                      {/* CMS page - only auto-detect */}
                      {activeTab === 'cms' && (
                        <>
                          <button
                            onClick={handleSetupPageAudits}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                            disabled={settingUp}
                          >
                            <Settings className={`w-5 h-5 ${settingUp ? 'animate-spin' : ''}`} />
                            {settingUp ? 'Setting up page audits...' : 'Setup Page Audits'}
                          </button>
                          <p className="text-xs text-gray-400 mt-3">
                            This will automatically discover and audit category, product, CMS, and blog pages.
                          </p>
                        </>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">No Audits Found</h3>
                  <p className="text-gray-500">No audit data available for this page type yet.</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AuditDashboard;
